import type {
  AssetItemConnection,
  TransferJobType,
} from '@frameio/graphql-client/generated';
import {
  QUEUED_TRANSFER_STATUSES,
  TERMINAL_TRANSFER_STATUSES,
  TRANSCODING_TRANSFER_STATUSES,
  TransferBatchStatus,
  TRANSFERRING_TRANSFER_STATUSES,
} from '@frameio/shared/transfers/states';
import type { MediaDataType } from '@frameio/shared/transfers/types';
import type { TransferBatch } from '../types/uploader';

/*
 * Create a smaller type based on TransferBatch for use in this helper.
 */
type TransferBatchHelper = Pick<
  TransferBatch,
  'id' | 'isPaused' | 'status' | 'progress' | 'hasViewed'
>;

type BatchesByStatus<T extends TransferBatchHelper> = {
  cancelled: T[];
  failures: T[];
  incomplete: T[];
  transcoding: T[];
  unviewed: T[];
  paused: T[];
};

export const isIdle = ({ status }: TransferBatchHelper) =>
  status === TransferBatchStatus.IDLE;

export const isQueued = ({ status }: TransferBatchHelper) =>
  QUEUED_TRANSFER_STATUSES.includes(status);

export const isTransferring = ({ status }: TransferBatchHelper) =>
  TRANSFERRING_TRANSFER_STATUSES.includes(status);

const isIncomplete = (batch: TransferBatchHelper) =>
  isQueued(batch) || isTransferring(batch);

const isTranscoding = ({ status }: TransferBatchHelper) =>
  TRANSCODING_TRANSFER_STATUSES.includes(status);

const isPaused = (batch: TransferBatchHelper) =>
  isTransferring(batch) && batch.isPaused;

export const isInTerminalState = ({ status }: TransferBatchHelper) =>
  TERMINAL_TRANSFER_STATUSES.includes(status);

export const hasPartialFailure = ({ fileCounts }: TransferBatch): boolean => {
  if (
    !fileCounts ||
    fileCounts.failed === null ||
    fileCounts.failed === undefined ||
    fileCounts.uploaded === null ||
    fileCounts.uploaded === undefined
  ) {
    return false;
  }
  return fileCounts.failed > 0 && fileCounts.uploaded > 0;
};

export const getIdleJobs = <T extends TransferBatchHelper>(jobs: T[]): T[] =>
  jobs.filter(isIdle);

export const getQueuedJobs = <T extends TransferBatchHelper>(jobs: T[]): T[] =>
  jobs.filter(isQueued);

export const getUploadingJobs = <T extends TransferBatchHelper>(
  jobs: T[]
): T[] => jobs.filter(isTransferring);

export const getIncompleteJobs = <T extends TransferBatchHelper>(
  jobs: T[]
): T[] => jobs.filter(isIncomplete);

export const getTranscodingJobs = <T extends TransferBatchHelper>(
  jobs: T[]
): T[] => jobs.filter(isTranscoding);

export const isEveryJobPaused = <T extends TransferBatchHelper>(jobs: T[]) =>
  jobs.length && jobs.every((job) => job.isPaused); // we check for jobs length also as .every will always return true on an empty array.

export const getTransferBatchType = (batch: TransferBatch): TransferJobType => {
  const { topLevelFileCount, topLevelFolderCount } = batch;
  if (topLevelFileCount === 0 && topLevelFolderCount === 1) {
    return 'FOLDER';
  }
  if (topLevelFileCount === 1 && topLevelFolderCount === 0) {
    return 'FILE';
  }
  return 'BATCH';
};

/**
 * Finds an asset by name in the paginated asset items and returns its media type.
 *
 * @param paginatedAssetItems - The paginated asset item connection from a transfer batch
 * @param name - The name of the asset to find
 * @returns The media `__typename` if found and supported, otherwise `undefined`
 */
export const getTransferBatchAssetType = (
  paginatedAssetItems: AssetItemConnection | null | undefined,
  name: string
): MediaDataType['__typename'] | undefined => {
  const transferBatchAsset = paginatedAssetItems?.nodes.find(
    (node) => node.asset?.name === name
  )?.asset;

  if (
    transferBatchAsset &&
    '__typename' in transferBatchAsset &&
    transferBatchAsset.__typename !== 'UnsupportedAsset' &&
    'media' in transferBatchAsset &&
    transferBatchAsset.media
  ) {
    return (transferBatchAsset.media as MediaDataType).__typename;
  }

  return undefined;
};

export const getTransferBatchesByStatus = <T extends TransferBatchHelper>(
  batches: T[] = []
): BatchesByStatus<T> => {
  const results: BatchesByStatus<T> = {
    cancelled: [],
    failures: [],
    incomplete: [],
    transcoding: [],
    unviewed: [],
    paused: [],
  };
  if (!batches?.length) return results;

  const batchesByStatus = batches.reduce((batchesGrouped, batch) => {
    const { status, hasViewed } = batch;

    if (!hasViewed && isInTerminalState(batch)) {
      batchesGrouped.unviewed.push(batch);
    }

    if (status === TransferBatchStatus.CANCELED) {
      batchesGrouped.cancelled.push(batch);
    } else if (status === TransferBatchStatus.FAILED) {
      batchesGrouped.failures.push(batch);
    } else if (batch?.progress) {
      // only testing batches with progress (e.g. local) below for performance reasons
      if (isTranscoding(batch)) {
        batchesGrouped.transcoding.push(batch);
      } else if (isIncomplete(batch)) {
        batchesGrouped.incomplete.push(batch);
        if (isPaused(batch)) {
          batchesGrouped.paused.push(batch);
        }
      }
    }

    return batchesGrouped;
  }, results);
  return batchesByStatus;
};
